import {SingleHeader} from "@components/header";
import {Contain} from "@components/layout";
import {Button, Col, Layout, Pagination, Row, Space} from "antd";
import {Helmet as Head} from "react-helmet-async";
import {useWindowSize} from "@components/window-size-provider";
import usePreferences from "@hooks/usePreferences";
import React, {useEffect, useMemo, useState} from "react";
import {SalesListFiltersPaginatedQuery, useGetIssuesSalesListQuery} from "@mwServices/Issue";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {useGetPageBySlugQuery} from "@services/Page";
import {formatDateUrl, formatNumber} from "@lib/format";
import {SalesListTypes} from "@lib/constants";
import {DiamondIcon, FlameIcon, InfoIcon, KeyIcon} from "@components/icons/Icons";
import {showInfoModal} from "@lib/showModal";
import {ClearFiltersBar} from "@components/issues-filter-drawer";
import {IssueGrid} from "@components/grid";
import {PAGINATION_PAGES} from "@components/grid/Grid";
import {scrollToThen} from "@lib/utils";
import AddComicModal from "@components/add-comic-modal";
import SalesListGridItem from "@mwPages/sales-lists/SalesListGridItem";
import SalesListFiltersDrawer from "@mwPages/sales-lists/SalesListFiltersDrawer";

const SalesList = ({ listType }) => {
    const pageQuery = useGetPageBySlugQuery({slug: listType.pageSlug});
    const { title, image } = useMemo(() => {
        if(pageQuery.data) {
            const data = pageQuery.data.layout_builder;
            return {title: data.title, image: data.image};
        }
        return {title: '', image: {src: '', focalPoint: {top:0, left:0}}};
    }, [pageQuery]);

    const windowDimensions = useWindowSize();
    const { itemCount, setItemCount } = usePreferences();

    const defaultFilters = useMemo(() => ({
        query: '',
        showVariants: true, //This means include variants in the results, not only variants.
        groupByGrade: false,
        onlyAllTimeHighSale: false,
        isHot: false,
        isKey: false,
        isRare: false,
        publishers: undefined,
        multipleDateRanges: undefined,
        inCollection: undefined,
        inWishlist: undefined,
        sort: 24, //default to sales list ids order in array.
    }), []);

    const [issueParams, setIssueParams] = useState<SalesListFiltersPaginatedQuery>({
        filters: { ...defaultFilters },
        listType: listType.listType,
        targetDate: formatDateUrl(new Date()),
        page: 1,
        perPage: itemCount,
    });

    useEffect(() => {
        setIssueParams({
            filters: { ...defaultFilters },
            listType: listType.listType,
            targetDate: formatDateUrl(new Date()),
            page: 1,
            perPage: itemCount,
        });
    }, [listType]);

    const issueQuery = useGetIssuesSalesListQuery(issueParams);

    const { pagination} = useMemo(() => {
        if(issueQuery.data) {
            return {
                pagination: issueQuery.data.pagination
            }
        }
        return { pagination: null };
    }, [issueQuery])

    const [addToCollectionIds, setAddToCollectionIds] = useState<number[]>([]);
    const subscriptionType = useSubscriptionType();

    // USEMEMO
    const showValueRibbon = useMemo(() => (
            subscriptionType.isStandard ||
            subscriptionType.isTrial ||
            subscriptionType.isUnlimited),
        [subscriptionType]
    );

    const showDesktopView = useMemo(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    return (
        <Layout className="page">
            <Head>
                <title>{title} - CovrPrice</title>
            </Head>
            <SingleHeader
                backButton={{
                    url: '',
                    name: 'back',
                    target: '_self'
                }}
                title={title}
                imgSrc={image.src}
                focalPoint={image.focalPoint}
                parallax={false}
                shade={`lightblue`}
            />
            <Contain>
                <Row justify="space-between">
                    <Col span={24}>
                        <div className="mar-5">
                            <Row className="flex-align-end">
                                <Col>
                                    <Space>
                                        <Button
                                            className="info-icon"
                                            type="link"
                                            icon={<InfoIcon className="iconLarge"/>}
                                            onClick={() => {
                                                showInfoModal({
                                                    title: listType.helpTitle,
                                                    content: listType.helpContent,
                                                });
                                            }}
                                        />

                                        <SalesListFiltersDrawer
                                            filters={issueParams.filters}
                                            setFilters={(filters) => setIssueParams({...issueParams, page: 1, filters})}
                                            listType={issueParams.listType}
                                        />

                                        {pagination && (
                                            <Pagination
                                                style={{whiteSpace: "nowrap"}}
                                                current={pagination.current_page}
                                                pageSize={pagination.per_page}
                                                total={pagination.total_items}
                                                onChange={(page) => {
                                                    if (issueParams && issueParams.page !== page) {
                                                        setIssueParams({...issueParams, page: page});
                                                    }
                                                }}
                                                simple
                                            />
                                        )}
                                    </Space>
                                </Col>
                            </Row>

                            <ClearFiltersBar
                                filters={issueParams.filters}
                                defaultFilters={defaultFilters}
                                issueCount={issueQuery.data ? issueQuery.data.overview.total : 0}
                                onClear={() => {
                                    setIssueParams({ ...issueParams, page: 1, filters: defaultFilters });
                                }}
                            />

                            <hr/>

                            <IssueGrid
                                isLoading={issueQuery.isLoading || issueQuery.isFetching}
                                data={issueQuery.data}
                                noDataMessage={``}
                                IssueRender={(props) =>
                                    (<>
                                        <SalesListGridItem
                                            {...props}
                                            windowDimensions={windowDimensions}
                                        />
                                    </>)}
                                getItemProps={(item) => ({
                                    key: item.id,
                                    listType,
                                    showValueRibbon,
                                    showDesktopView,
                                })}
                                paginationType={PAGINATION_PAGES}
                                onPaginationChange={(page => {
                                    if (issueParams && issueParams.page !== page) {
                                        scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                            setIssueParams({...issueParams, page: page});
                                        });
                                    }
                                })}
                                onPaginationSizeChange={(current: number, perPage: number) => {
                                    if (issueParams && issueParams.perPage !== perPage) {
                                        scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                            setIssueParams({...issueParams, page: 1, perPage: perPage});
                                            setItemCount(perPage);
                                        });
                                    }
                                }}
                            />

                            {addToCollectionIds.length > 0
                                && <AddComicModal isModalOpen={true} issueIds={addToCollectionIds} onCancel={() => setAddToCollectionIds([])}/>
                            }
                        </div>
                    </Col>
                </Row>
            </Contain>
        </Layout>
    );
};

export default SalesList;
